/**
 * Download helpers shared by the result grid and the chart.
 *
 * Both answer types can be exported, and a chart can be exported *with* its
 * data, so the CSV builder and the browser-download plumbing live here rather
 * than being duplicated in ChatResultTable.js and ChatChart.js.
 */
import config from "./chatAssistantConfig";

/** Escapes one value for CSV (RFC 4180). */
export function csvCell(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Builds a CSV string. A UTF-8 BOM is prepended so Excel on Windows opens
 * non-ASCII names (e.g. "Buffa Colomé,Bernardo") without mangling them.
 */
export function buildCsv(columns, rows) {
  const header = columns.map(csvCell).join(",");
  const body = rows.map((r) => columns.map((c) => csvCell(r[c])).join(","));
  return `﻿${[header, ...body].join("\r\n")}`;
}

export function textToBase64(text) {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return window.btoa(binary);
}

export function slugify(text) {
  return (text || "result")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

/** `ds-assistant-open-demand-by-skill-tower-2026-10-03` */
export function exportBaseName(title) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${config.CSV_FILENAME_PREFIX}-${slugify(title)}-${stamp}`;
}

/** Clicks a temporary anchor, then cleans up after itself. */
function triggerDownload(href, filename, revoke) {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (revoke) URL.revokeObjectURL(href);
}

/** Downloads `rows` as a CSV named after the answer's title. */
export function downloadCsv(columns, rows, title) {
  const blob = new Blob([buildCsv(columns, rows)], {
    type: "text/csv;charset=utf-8;",
  });
  triggerDownload(URL.createObjectURL(blob), `${exportBaseName(title)}.csv`, true);
}

/**
 * Downloads a DOM node as a JPEG.
 *
 * JPEG (not PNG) because that is what was asked for, and because these charts
 * get pasted into decks and mails. `html-to-image` is already a dependency of
 * this app, used by the report pages for the same purpose. The explicit white
 * background matters: JPEG has no alpha, so without it transparent areas come
 * out black.
 *
 * @returns {Promise<void>} rejects if the node could not be rasterised
 */
export async function downloadNodeAsJpeg(node, title) {
  if (!node) throw new Error("Nothing to export");
  const { toJpeg } = await import("html-to-image");
  const dataUrl = await toJpeg(node, {
    quality: 0.95,
    backgroundColor: "#ffffff",
    pixelRatio: 2, // Readable when pasted into a slide at full width.
    cacheBust: true,
  });
  triggerDownload(dataUrl, `${exportBaseName(title)}.jpeg`);
}
